import { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { Button as MuiButton, Grid, IconButton, Stack } from '@mui/material';

import { IcDeleteTrash } from '@/assets/images';
import { RenderFormField } from '@/components';
import { Button } from '@/components/TailwindComponents';
import { IPhoneNumber, PhoneNumberType } from '@/models';
import { colors } from '@/theme/variables';

import { MasterNamePhoneNumberFormFields } from '../data';

const EMPTY_PHONE = { type: PhoneNumberType.OTHER, phoneNumber: '' };

export const MasterNamePhoneNumberForm = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'phoneNumbers',
  });
  const phoneNumbers = (useWatch({ control, name: 'phoneNumbers' }) ||
    []) as IPhoneNumber[];

  useEffect(() => {
    if (fields.length === 0) {
      append({ ...EMPTY_PHONE, isPrimary: true }, { shouldFocus: false });
    }
  }, [fields, append]);

  useEffect(() => {
    const select: any = MasterNamePhoneNumberFormFields.find(
      (e) => e.type.toLowerCase() === 'select',
    );
    select?.items.forEach((item: any) => {
      if (item.label.toLowerCase() === 'caller') item.disabled = true;
    });
  }, []);

  const setPrimary = (index: number) =>
    replace(
      phoneNumbers.map((phone, i) => ({ ...phone, isPrimary: i === index })),
    );

  const removePhone = (index: number) => {
    const remaining = phoneNumbers.filter((_, i) => i !== index);
    if (remaining.length === 0) {
      replace([{ ...EMPTY_PHONE, isPrimary: true }]);
      return;
    }
    const hasPrimary = remaining.some((p) => p.isPrimary === true);
    replace(
      hasPrimary
        ? remaining
        : remaining.map((phone, i) => ({ ...phone, isPrimary: i === 0 })),
    );
  };

  // A newly added number becomes the default (primary) unless the user later
  // picks another via "Set as primary"; demote any existing rows.
  const addPhone = () =>
    replace([
      ...phoneNumbers.map((phone) => ({ ...phone, isPrimary: false })),
      { ...EMPTY_PHONE, isPrimary: true },
    ]);

  return (
    <Stack my={2}>
      {fields.map((phoneField, index) => {
        const isPrimary = phoneNumbers[index]?.isPrimary === true;
        const isCaller = 'type' in phoneField && phoneField.type === 'CALLER';

        return (
          <Grid
            container
            columnSpacing={2}
            alignItems="center"
            key={phoneField.id}
          >
            {MasterNamePhoneNumberFormFields.map((field, idx) => (
              <Grid key={`${phoneField.id}-${idx}`} xs={4} item>
                <RenderFormField
                  {...field}
                  name={`phoneNumbers.${index}.${field.name}`}
                  disabled={disabled || isCaller}
                />
              </Grid>
            ))}
            <Grid xs={1} item style={{ display: 'flex' }}>
              <IconButton
                onClick={() => removePhone(index)}
                disabled={disabled}
                sx={{ color: colors.error.main, mt: 2, alignSelf: 'center' }}
              >
                <IcDeleteTrash />
              </IconButton>
            </Grid>
            <Grid xs={2} item style={{ marginTop: '8px' }}>
              <MuiButton
                variant="contained"
                color="error"
                size="small"
                disabled={disabled || isPrimary}
                onClick={() => setPrimary(index)}
                sx={{
                  textTransform: 'none',
                  '&.Mui-disabled': isPrimary
                    ? {
                        backgroundColor: '#EDF1F4',
                        color: '#AEB7C0',
                        opacity: 1,
                      }
                    : undefined,
                }}
              >
                {isPrimary ? 'Primary' : 'Set as primary'}
              </MuiButton>
            </Grid>
          </Grid>
        );
      })}
      <Button
        color="error"
        type="button"
        size="sm"
        variant="secondary"
        className="text-sm w-fit"
        onClick={addPhone}
        disabled={disabled}
      >
        + Add phone number
      </Button>
    </Stack>
  );
};
